import { useEffect, useMemo } from "react";
import { X } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { fmtN, fmtPct, fmtInt, money, attColor, penColor, C, REGION_COLORS } from "./osUtils.jsx";

const tooltipStyle = { background: "rgb(var(--th-solid))", border: "1px solid rgb(var(--th-border) / 0.14)", borderRadius: 10, fontSize: 11, color: "rgb(var(--th-strong))" };
const tick = { fill: "#7a8fb5", fontSize: 9, fontFamily: "JetBrains Mono" };

export default function OsAgentDrawer({ data, u, month, onClose }) {
  const agent = data.agents[u];
  useEffect(() => { const h = (e) => e.key === "Escape" && onClose(); window.addEventListener("keydown", h); return () => window.removeEventListener("keydown", h); }, [onClose]);

  const k = useMemo(() => {
    const S = data.sales.filter((s) => s.u === u && s.d.startsWith(month));
    const T = data.targets.filter((t) => t.u === u && (!month || t.m === month));
    const n = S.reduce((a, s) => a + s.n, 0);
    const tp = new Set(T.map((t) => t.p));
    const nTP = S.filter((s) => tp.has(s.p)).reduce((a, s) => a + s.n, 0);
    const target = T.reduce((a, t) => a + t.t, 0);
    const churned = data.churn.filter((c) => c.u === u && (!month || c.m === month)).reduce((a, c) => a + c.n, 0);
    const remaining = data.wd[month]?.remaining || 0;
    const Co = data.commission.filter((c) => c.u === u && (!month || c.m === month)); const co = Co.length ? Co.reduce((a, c) => ({ core: a.core + c.core, pen_amt: a.pen_amt + c.pen_amt, net: a.net + c.net, share: a.share + c.share, pen: null }), { core: 0, pen_amt: 0, net: 0, share: 0 }) : null; if (co) co.pen = co.core ? (100 * co.pen_amt) / co.core : null;
    return {
      n, nTP, target, att: target ? (100 * nTP) / target : null, deficit: Math.max(target - nTP, 0),
      req: target && remaining > 0 ? Math.max(target - nTP, 0) / remaining : null,
      churned, churnPct: n + churned ? (100 * churned) / (n + churned) : null, co,
    };
  }, [data, u, month]);

  const daily = useMemo(() => {
    const by = {};
    data.sales.forEach((s) => { if (s.u === u && s.d.startsWith(month)) by[s.d] = (by[s.d] || 0) + s.n; });
    return Object.keys(by).sort().map((d) => ({ d: d.slice(8), sales: by[d] }));
  }, [data, u, month]);

  const mix = useMemo(() => {
    const by = {};
    data.targets.forEach((t) => { if (t.u === u && (!month || t.m === month)) (by[t.p] = by[t.p] || { p: t.p, t: 0, ach: 0, churned: 0 }).t += t.t; });
    data.sales.forEach((s) => { if (s.u === u && s.d.startsWith(month)) (by[s.p] = by[s.p] || { p: s.p, t: 0, ach: 0, churned: 0 }).ach += s.n; });
    data.churn.forEach((c) => { if (c.u === u && (!month || c.m === month)) (by[c.p] = by[c.p] || { p: c.p, t: 0, ach: 0, churned: 0 }).churned += c.n; });
    return Object.values(by).map((r) => ({ ...r, att: r.t ? (100 * r.ach) / r.t : null })).sort((a, b) => b.t + b.ach - (a.t + a.ach));
  }, [data, u, month]);

  const churned = useMemo(() => (data.churn_list[u] || []).filter((c) => c.d.startsWith(month)).sort((a, b) => (a.d < b.d ? 1 : -1)), [data, u, month]);
  const history = useMemo(() => data.commission.filter((c) => c.u === u).sort((a, b) => (a.m < b.m ? -1 : 1)), [data, u]);

  const chips = [
    { label: "Target", v: fmtInt(k.target) }, { label: "Achieved", v: k.target ? `${fmtInt(k.n)} · ${fmtInt(k.nTP)} on target lines` : fmtInt(k.n) },
    { label: "Attain %", v: fmtPct(k.att), c: attColor(k.att) }, { label: "Deficit", v: fmtInt(k.deficit) },
    { label: "Req/day", v: k.req == null ? "—" : fmtN(k.req, 1) }, { label: "Churn-flagged", v: k.churned ? `${k.churned} · ${fmtPct(k.churnPct)}` : "0", c: k.churned ? C.red : undefined },
    { label: "Core Commission", v: money(k.co?.core) }, { label: "Penalty", v: k.co ? `${fmtPct(k.co.pen)} · ${money(k.co.pen_amt)}` : "—", c: penColor(k.co?.pen) },
    { label: "Net Payout", v: money(k.co?.net), c: C.yellow }, { label: "Agent Share", v: money(k.co?.share), c: C.cyan },
  ];

  if (!agent) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="os-drawer">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative flex h-full w-full max-w-[1500px] flex-col overflow-y-auto border-l border-thborder/15 bg-appbg shadow-2xl lg:w-[88vw] xl:w-[80vw]">
        <div className="sticky top-0 z-10 border-b border-thborder/10 bg-appbg/95 px-6 py-4 backdrop-blur">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="font-heading text-xl font-bold text-thstrong">{agent.name || u}</h2>
              <p className="font-mono-hud mt-1 text-[10px] uppercase tracking-[0.2em] text-thdim">
                {u.startsWith("~") ? "Not in Sales Dump" : agent.cisco || u} · <span style={{ color: REGION_COLORS[agent.region] || C.grey }}>{agent.region || "—"}</span> · {month || "All months"}
              </p>
            </div>
            <button data-testid="os-drawer-close" onClick={onClose} className="rounded-md p-2 text-thmute hover:bg-thhover/10 hover:text-thstrong"><X className="h-5 w-5" /></button>
          </div>
        </div>

        <div className="space-y-6 px-6 py-5">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
            {chips.map((c) => (
              <div key={c.label} className="rounded-xl border border-thborder/10 bg-thinput px-3 py-2">
                <p className="font-mono-hud text-[9px] uppercase tracking-[0.12em] text-thdim">{c.label}</p>
                <p className="font-mono-hud text-sm font-semibold" style={{ color: c.c || "rgb(var(--th-strong))" }}>{c.v}</p>
              </div>
            ))}
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <div className="mk-card p-4">
              <p className="font-mono-hud mb-2 text-[10px] uppercase tracking-[0.2em] text-thdim">Daily counted sales · {month}</p>
              {daily.length === 0 ? <p className="text-xs text-thdim">No counted sales this month.</p> : (
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={100} initialDimension={{ width: 1, height: 1 }}>
                    <BarChart data={daily} margin={{ top: 4, right: 8, bottom: 0, left: -14 }}>
                      <XAxis dataKey="d" tick={tick} interval="preserveStartEnd" /><YAxis tick={tick} allowDecimals={false} />
                      <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: "rgb(var(--th-mute))" }} />
                      <Bar dataKey="sales" fill={C.green} radius={[2, 2, 0, 0]} name="Sales" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
            <div className="mk-card p-4">
              <p className="font-mono-hud mb-2 text-[10px] uppercase tracking-[0.2em] text-thdim">Product mix · target vs achieved</p>
              {mix.length === 0 ? <p className="text-xs text-thdim">No targets or sales this month.</p> : (
                <div className="max-h-64 space-y-2 overflow-auto pr-1">
                  {mix.map((r) => {
                    const max = Math.max(r.t, r.ach) || 1;
                    return (
                      <div key={r.p} className="rounded-xl border border-thborder/10 bg-thinput p-3">
                        <div className="mb-1.5 flex items-center justify-between">
                          <p className="text-xs font-medium text-thstrong">{r.p}</p>
                          <p className="font-mono-hud text-[10px]" style={{ color: attColor(r.att) }}>{fmtInt(r.ach)} / {r.t ? fmtInt(r.t) : "—"} · {fmtPct(r.att)}{r.churned ? <span className="text-[#F43F5E]"> · {r.churned} churned</span> : null}</p>
                        </div>
                        <div className="mb-1 h-2 rounded-full bg-thhover/10"><div className="h-2 rounded-full" style={{ width: `${(100 * r.t) / max}%`, background: C.grey }} /></div>
                        <div className="h-2 rounded-full bg-thhover/10"><div className="h-2 rounded-full" style={{ width: `${(100 * r.ach) / max}%`, background: attColor(r.att) }} /></div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <div className="mk-card p-4">
              <p className="font-mono-hud mb-2 text-[10px] uppercase tracking-[0.2em] text-thdim">Commission history</p>
              {history.length === 0 ? <p className="text-xs text-thdim">No Commission rows for this agent.</p> : (
                <table className="w-full text-left"><thead><tr className="border-b border-thborder/10">{["Month", "Core", "Pen %", "Pen amt", "Net", "Share"].map((h, i) => <th key={h} className={`font-mono-hud px-2 py-1.5 text-[9px] uppercase tracking-[0.12em] text-thdim ${i ? "text-right" : ""}`}>{h}</th>)}</tr></thead>
                  <tbody>{history.map((c) => <tr key={c.m} className="border-b border-thborder/5"><td className="px-2 py-1.5 font-mono-hud text-xs text-thstrong">{c.m}</td><td className="px-2 py-1.5 text-right font-mono-hud text-xs">{money(c.core)}</td><td className="px-2 py-1.5 text-right font-mono-hud text-xs" style={{ color: penColor(c.pen) }}>{fmtPct(c.pen)}</td><td className="px-2 py-1.5 text-right font-mono-hud text-xs">{money(c.pen_amt)}</td><td className="px-2 py-1.5 text-right font-mono-hud text-xs font-semibold text-thstrong">{money(c.net)}</td><td className="px-2 py-1.5 text-right font-mono-hud text-xs">{money(c.share)}</td></tr>)}</tbody></table>
              )}
            </div>
            <div className="mk-card p-4">
              <p className="font-mono-hud mb-2 text-[10px] uppercase tracking-[0.2em] text-thdim">Churn-flagged sales ({churned.length}) · MSISDNs masked</p>
              {churned.length === 0 ? <p className="text-xs text-thdim">No churn-flagged sales this month.</p> : (
                <div className="max-h-64 space-y-1.5 overflow-auto pr-1">
                  {churned.map((c, i) => (
                    <div key={i} className="flex items-center justify-between rounded-xl border border-thborder/10 bg-thinput px-3 py-2">
                      <p className="text-xs text-thbody">{c.p} <span className="text-thdim">· {c.tariff}</span> <span className="font-mono-hud text-thdim">{c.msk}</span></p>
                      <p className="font-mono-hud text-[10px] text-thmute">{c.d}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
